import { useState } from "react";
import { Button, Col, FormFeedback, Input, Label, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faPlus, faTrash, faXmark, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import moment from "moment-timezone";
import styles from "./style.module.scss";
import InternalDocumentPicker from "./InternalDocumentPicker";
import {
    MAX_DOCUMENT_REFERENCES,
    REFERENCE_NUMBER_MAX_LENGTH,
    REFERENCE_REASON_MAX_LENGTH,
    StoredReference,
    ReferenceLinkedDocumentKind,
    ReferenceSource,
    generateReferenceId,
} from "@utils/DocumentReferences";
import {
    REFERENCE_CODE_OPTIONS,
    REFERENCE_TYPE_OPTIONS,
    REFERENCES_INFORMATION,
    getReferenceCodeLabel,
    getReferenceTypeLabel,
} from "@utils/Constants";

type DocumentReferencesEditorProps = {
    value: StoredReference[];
    onChange: (next: StoredReference[]) => void;
    readonly?: boolean;
    maxItems?: number;
};

type EditorTab = "internal" | "external";

type DraftReference = {
    id?: string;
    source: ReferenceSource;
    tipoDoc: string;
    numero: string;
    fechaEmisionUTC: string;
    codigo: string;
    razon: string;
    linkedDocumentId?: string;
    linkedDocumentKind?: ReferenceLinkedDocumentKind;
    isLinkedDocumentRejected?: boolean;
    suggestedTipoDoc?: string;
};

type FieldErrors = Partial<Record<keyof DraftReference, string>>;

const EMPTY_DRAFT: DraftReference = {
    source: "manual_external",
    tipoDoc: "",
    numero: "",
    fechaEmisionUTC: "",
    codigo: "",
    razon: "",
};

const RFC3339_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";
const DATETIME_INPUT_FORMAT = "YYYY-MM-DDTHH:mm";

const toInputDateTime = (rfc3339: string): string => {
    if (!rfc3339) return "";
    const m = moment.parseZone(rfc3339);
    if (!m.isValid()) return "";
    return m.format(DATETIME_INPUT_FORMAT);
};

const fromInputDateTime = (value: string): string => {
    if (!value) return "";
    const m = moment(value, DATETIME_INPUT_FORMAT);
    if (!m.isValid()) return "";
    return m.format(RFC3339_FORMAT);
};

const formatDisplayDate = (rfc3339: string): string => {
    if (!rfc3339) return "";
    const m = moment.parseZone(rfc3339);
    if (!m.isValid()) return rfc3339;
    return m.format("DD/MM/YYYY HH:mm");
};

const validateDraft = (draft: DraftReference): FieldErrors => {
    const errors: FieldErrors = {};
    if (!draft.tipoDoc) errors.tipoDoc = "Seleccione el tipo de documento";
    if (!draft.codigo) errors.codigo = "Seleccione el código de referencia";
    if (!draft.numero || draft.numero.length === 0) {
        errors.numero = "Ingrese el número del documento";
    } else if (draft.numero.length > REFERENCE_NUMBER_MAX_LENGTH) {
        errors.numero = `Máximo ${REFERENCE_NUMBER_MAX_LENGTH} caracteres`;
    }
    if (!draft.fechaEmisionUTC) {
        errors.fechaEmisionUTC = "Indique la fecha de emisión";
    }
    if (!draft.razon || draft.razon.length === 0) {
        errors.razon = "Ingrese la razón";
    } else if (draft.razon.length > REFERENCE_REASON_MAX_LENGTH) {
        errors.razon = `Máximo ${REFERENCE_REASON_MAX_LENGTH} caracteres`;
    }
    return errors;
};

const draftFromReference = (ref: StoredReference): DraftReference => ({
    id: ref.id,
    source: ref.source,
    tipoDoc: ref.tipoDoc,
    numero: ref.numero,
    fechaEmisionUTC: ref.fechaEmisionUTC,
    codigo: ref.codigo,
    razon: ref.razon,
    linkedDocumentId: ref.linkedDocumentId,
    linkedDocumentKind: ref.linkedDocumentKind,
});

const referenceFromDraft = (draft: DraftReference): StoredReference => ({
    id: draft.id || generateReferenceId(),
    source: draft.source,
    tipoDoc: draft.tipoDoc,
    numero: draft.numero,
    fechaEmisionUTC: draft.fechaEmisionUTC,
    codigo: draft.codigo,
    razon: draft.razon,
    linkedDocumentId: draft.linkedDocumentId,
    linkedDocumentKind: draft.linkedDocumentKind,
});

const DocumentReferencesEditor = (props: DocumentReferencesEditorProps) => {
    const { value, onChange, readonly = false, maxItems = MAX_DOCUMENT_REFERENCES } = props;

    const [isFormOpen, setFormOpen] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<EditorTab>("internal");
    const [draft, setDraft] = useState<DraftReference>(EMPTY_DRAFT);
    const [errors, setErrors] = useState<FieldErrors>({});

    const limitReached = value.length >= maxItems;

    const resetDraft = () => {
        setDraft(EMPTY_DRAFT);
        setErrors({});
        setActiveTab("internal");
    };

    const openCreateForm = () => {
        resetDraft();
        setFormOpen(true);
    };

    const openEditForm = (ref: StoredReference) => {
        setDraft(draftFromReference(ref));
        setErrors({});
        setActiveTab(ref.source === "manual_internal" ? "internal" : "external");
        setFormOpen(true);
    };

    const closeForm = () => {
        setFormOpen(false);
        resetDraft();
    };

    const handleDraftChange = (field: keyof DraftReference, fieldValue: string) => {
        setDraft((prev) => ({ ...prev, [field]: fieldValue }));
    };

    const handleSaveDraft = () => {
        const validationErrors = validateDraft(draft);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        const next = referenceFromDraft(draft);
        if (draft.id) {
            onChange(value.map((ref) => (ref.id === draft.id ? next : ref)));
        } else {
            onChange([...value, next]);
        }
        closeForm();
    };

    const handleDelete = (refId: string) => {
        onChange(value.filter((ref) => ref.id !== refId));
    };

    const handleInternalDocumentResolved: React.ComponentProps<
        typeof InternalDocumentPicker
    >["onDocumentResolved"] = (info, meta) => {
        setDraft((prev) => ({
            ...prev,
            source: "manual_internal",
            tipoDoc: info.tipoDoc,
            numero: info.numero,
            fechaEmisionUTC: info.fechaEmisionUTC,
            linkedDocumentId: meta.linkedDocumentId,
            linkedDocumentKind: meta.linkedDocumentKind,
            isLinkedDocumentRejected: meta.isRejectedByHacienda,
            suggestedTipoDoc: info.tipoDoc,
        }));
        setErrors((prev) => ({ ...prev, tipoDoc: undefined, numero: undefined, fechaEmisionUTC: undefined }));
    };

    const handleTabChange = (tab: EditorTab) => {
        setActiveTab(tab);
        if (tab === "external") {
            setDraft((prev) => ({
                ...prev,
                source: "manual_external",
                linkedDocumentId: undefined,
                linkedDocumentKind: undefined,
            }));
        } else if (!draft.id) {
            setDraft((prev) => ({
                ...prev,
                source: "manual_internal",
            }));
        }
    };

    const renderReferenceCard = (ref: StoredReference) => (
        <div key={ref.id} className={styles.referenceCard}>
            <div className={styles.referenceCardMeta}>
                <div className={styles.metaPrimary}>
                    <span className={styles.badge}>{getReferenceTypeLabel(ref.tipoDoc)}</span>
                    {ref.source === "auto" && (
                        <span className={cx(styles.badge, styles.badgeAuto)}>Auto</span>
                    )}
                    <span className={styles.metaNumber} title={ref.numero}>
                        {ref.numero}
                    </span>
                </div>
                <div className={styles.metaSecondary}>
                    <span>{formatDisplayDate(ref.fechaEmisionUTC)}</span>
                    <span>{getReferenceCodeLabel(ref.codigo)}</span>
                </div>
                {ref.razon && <div className={styles.metaRazon}>{ref.razon}</div>}
            </div>
            {!readonly && (
                <div className={styles.referenceActions}>
                    <Button
                        size="sm"
                        color="primary"
                        type="button"
                        onClick={() => openEditForm(ref)}
                        title="Editar referencia"
                    >
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                    <Button
                        size="sm"
                        color="danger"
                        type="button"
                        onClick={() => handleDelete(`${ref.id}`)}
                        title="Eliminar referencia"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            )}
        </div>
    );

    const renderInternalTab = () => (
        <>
            <InternalDocumentPicker
                onDocumentResolved={handleInternalDocumentResolved}
            />
            {draft.linkedDocumentId && (
                <>
                    <div className={styles.previewCard}>
                        <strong>{getReferenceTypeLabel(draft.tipoDoc)}</strong>
                        <span className={styles.metaNumber} title={draft.numero}>{draft.numero}</span>
                        <span className={styles.fieldHint}>
                            Fecha: {formatDisplayDate(draft.fechaEmisionUTC)}
                        </span>
                        {draft.isLinkedDocumentRejected && (
                            <span className={cx(styles.badge, styles.badgeAuto)}>
                                Rechazado por Hacienda
                            </span>
                        )}
                    </div>
                    <Row className="g-2 mt-2">
                        <Col xs={12}>
                            <Label className={styles.fieldLabel} htmlFor="ref-int-tipoDoc">
                                Tipo de documento
                            </Label>
                            <Input
                                id="ref-int-tipoDoc"
                                type="select"
                                value={draft.tipoDoc}
                                invalid={!!errors.tipoDoc}
                                onChange={(e) => handleDraftChange("tipoDoc", e.target.value)}
                            >
                                <option value="">Seleccione...</option>
                                {REFERENCE_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.value} - {opt.label}
                                    </option>
                                ))}
                            </Input>
                            {errors.tipoDoc && <FormFeedback>{errors.tipoDoc}</FormFeedback>}
                            {draft.suggestedTipoDoc && draft.tipoDoc !== draft.suggestedTipoDoc && (
                                <div className={styles.fieldHint}>
                                    Sugerido por el sistema: {draft.suggestedTipoDoc} - {getReferenceTypeLabel(draft.suggestedTipoDoc)}
                                    {" "}
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() =>
                                            handleDraftChange(
                                                "tipoDoc",
                                                draft.suggestedTipoDoc || REFERENCES_INFORMATION.Invoice,
                                            )
                                        }
                                    >
                                        Restaurar
                                    </button>
                                </div>
                            )}
                        </Col>
                    </Row>
                </>
            )}
            {renderSharedFields()}
        </>
    );

    const renderExternalTab = () => (
        <>
            <Row className="g-2">
                <Col xs={12}>
                    <Label className={styles.fieldLabel} htmlFor="ref-ext-tipoDoc">
                        Tipo de documento
                    </Label>
                    <Input
                        id="ref-ext-tipoDoc"
                        type="select"
                        value={draft.tipoDoc}
                        invalid={!!errors.tipoDoc}
                        onChange={(e) => handleDraftChange("tipoDoc", e.target.value)}
                    >
                        <option value="">Seleccione...</option>
                        {REFERENCE_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.value} - {opt.label}
                            </option>
                        ))}
                    </Input>
                    {errors.tipoDoc && <FormFeedback>{errors.tipoDoc}</FormFeedback>}
                </Col>
                <Col xs={12} md={6}>
                    <Label className={styles.fieldLabel} htmlFor="ref-ext-numero">
                        Número
                    </Label>
                    <Input
                        id="ref-ext-numero"
                        value={draft.numero}
                        invalid={!!errors.numero}
                        maxLength={REFERENCE_NUMBER_MAX_LENGTH}
                        onChange={(e) => handleDraftChange("numero", e.target.value)}
                    />
                    {errors.numero && <FormFeedback>{errors.numero}</FormFeedback>}
                </Col>
                <Col xs={12} md={6}>
                    <Label className={styles.fieldLabel} htmlFor="ref-ext-fecha">
                        Fecha de emisión
                    </Label>
                    <Input
                        id="ref-ext-fecha"
                        type="datetime-local"
                        value={toInputDateTime(draft.fechaEmisionUTC)}
                        invalid={!!errors.fechaEmisionUTC}
                        onChange={(e) =>
                            handleDraftChange("fechaEmisionUTC", fromInputDateTime(e.target.value))
                        }
                    />
                    {errors.fechaEmisionUTC && (
                        <FormFeedback>{errors.fechaEmisionUTC}</FormFeedback>
                    )}
                </Col>
            </Row>
            {renderSharedFields()}
        </>
    );

    const renderSharedFields = () => (
        <Row className="g-2 mt-2">
            <Col xs={12}>
                <Label className={styles.fieldLabel} htmlFor="ref-codigo">
                    Código de referencia
                </Label>
                <Input
                    id="ref-codigo"
                    type="select"
                    value={draft.codigo}
                    invalid={!!errors.codigo}
                    onChange={(e) => handleDraftChange("codigo", e.target.value)}
                >
                    <option value="">Seleccione...</option>
                    {REFERENCE_CODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.value} - {opt.label}
                        </option>
                    ))}
                </Input>
                {errors.codigo && <FormFeedback>{errors.codigo}</FormFeedback>}
            </Col>
            <Col xs={12}>
                <Label className={styles.fieldLabel} htmlFor="ref-razon">
                    Razón
                </Label>
                <Input
                    id="ref-razon"
                    type="textarea"
                    rows={2}
                    value={draft.razon}
                    invalid={!!errors.razon}
                    maxLength={REFERENCE_REASON_MAX_LENGTH}
                    onChange={(e) => handleDraftChange("razon", e.target.value)}
                />
                {errors.razon && <FormFeedback>{errors.razon}</FormFeedback>}
                <div className={styles.fieldHint}>
                    {draft.razon.length}/{REFERENCE_REASON_MAX_LENGTH}
                </div>
            </Col>
        </Row>
    );

    return (
        <div className={styles.editor}>
            <div className={styles.headerRow}>
                <span className={styles.counter}>
                    {value.length}/{maxItems} referencias
                </span>
                {!readonly && (
                    <Button
                        color="success"
                        type="button"
                        onClick={openCreateForm}
                        disabled={limitReached || isFormOpen}
                        title={limitReached ? `Máximo ${maxItems} referencias` : undefined}
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Agregar referencia
                    </Button>
                )}
            </div>

            {readonly && (
                <div className={styles.readonlyNotice}>
                    El documento ya fue emitido. Las referencias son solo de lectura.
                </div>
            )}

            {value.length === 0 ? (
                <div className={styles.emptyState}>
                    No hay referencias configuradas para este documento.
                </div>
            ) : (
                value.map(renderReferenceCard)
            )}

            {!readonly && isFormOpen && (
                <div className={styles.formCard}>
                    <div className={styles.tabsRow}>
                        <button
                            type="button"
                            className={cx(styles.tabButton, {
                                [styles.tabButtonActive]: activeTab === "internal",
                            })}
                            onClick={() => handleTabChange("internal")}
                        >
                            Documento del sistema
                        </button>
                        <button
                            type="button"
                            className={cx(styles.tabButton, {
                                [styles.tabButtonActive]: activeTab === "external",
                            })}
                            onClick={() => handleTabChange("external")}
                        >
                            Referencia externa
                        </button>
                    </div>

                    {activeTab === "internal" ? renderInternalTab() : renderExternalTab()}

                    <div className={styles.formActions}>
                        <Button color="secondary" type="button" onClick={closeForm}>
                            <FontAwesomeIcon icon={faXmark} className="me-2" />
                            Cancelar
                        </Button>
                        <Button color="primary" type="button" onClick={handleSaveDraft}>
                            <FontAwesomeIcon icon={faFloppyDisk} className="me-2" />
                            {draft.id ? "Actualizar" : "Agregar"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentReferencesEditor;
